import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import type { PersonInternal } from "@event-planner/shared";

/**
 * Reads + writes for `personInternals/{personId}` (ADR-136).
 * Organizer/admin client writes are allowed by rules; this service is
 * the admin's write surface for the internal tier — notes and, since
 * the ADR-172 review-first revision, the HubSpot link pointer (which
 * commits on the person form's Save, never from a server-side flow).
 */
export async function getPersonInternal(
  personId: string,
): Promise<PersonInternal | null> {
  const snap = await getDoc(doc(db, "personInternals", personId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as PersonInternal;
}

/**
 * Deliberately narrow: only the fields this write surface owns. In
 * particular `companions` must NOT be included — an earlier version sent
 * `companions: []` on every save, and the merge overwrote real companion
 * data seeded by `ensurePersonByEmail` and read by mobile.
 */
export interface SavePersonInternalInput {
  notes: string | null;
  /**
   * HubSpot link pointer (ADR-172). Omit to leave the link untouched;
   * pass values to establish it. Cleared via `unlinkHubSpotContact`.
   */
  hubspotContactId?: string;
  hubspotRepcrd?: string | null;
}

export async function savePersonInternal(
  personId: string,
  data: SavePersonInternalInput,
): Promise<void> {
  const ref = doc(db, "personInternals", personId);
  const snap = await getDoc(ref);
  const update: Record<string, unknown> = {
    ...data,
    updatedAt: serverTimestamp(),
  };
  if (!snap.exists()) {
    // Lazy-init the house seed shape so a doc born from a notes/link
    // save still validates against the canonical schema.
    update.notes = data.notes ?? null;
    update.tags = [];
    update.companions = [];
    update.createdAt = serverTimestamp();
  }
  await setDoc(ref, update, { merge: true });
}

/**
 * Severs the HubSpot link (clears the pointer fields). Previously
 * pulled profile data stays — unlinking is not an undo. Idempotent:
 * no doc or no link is success with nothing written.
 */
export async function unlinkHubSpotContact(personId: string): Promise<void> {
  const ref = doc(db, "personInternals", personId);
  const snap = await getDoc(ref);
  if (!snap.exists() || !snap.data()?.hubspotContactId) return;
  await setDoc(
    ref,
    {
      hubspotContactId: null,
      hubspotRepcrd: null,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}
